import { useCallback, useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";
import { imageProxy } from "@/data/site";
import { useLang } from "@/lib/i18n";

// Real customer water-heater rebuild — corroded supply lines & sediment-clogged
// expansion tank vs. fully repiped copper with new tank, gauge and shut-offs.
const PAIR = {
  beforeRaw:
    "https://customer-assets.emergentagent.com/job_modern-faster-pipes/artifacts/9cl7s64t_Before.jpg",
  afterRaw:
    "https://customer-assets.emergentagent.com/job_modern-faster-pipes/artifacts/6rd4hg6s_After.jpg",
};

export default function BeforeAfter() {
  const { t, lang } = useLang();
  const [pct, setPct] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const beforeImg = imageProxy(PAIR.beforeRaw, { w: 1200, q: 82 });
  const afterImg = imageProxy(PAIR.afterRaw, { w: 1200, q: 82 });

  const moveTo = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const next = ((clientX - r.left) / r.width) * 100;
    setPct(Math.max(0, Math.min(100, next)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    moveTo(e.clientX);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    moveTo(e.clientX);
  };
  const onPointerUp = () => {
    dragging.current = false;
  };

  return (
    <section
      data-testid="before-after"
      className="section-padding bg-[radial-gradient(ellipse_at_top,#FFFBEB_0%,#F8FAFC_55%,#EFF6FF_100%)] relative"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/50 to-transparent" />
      <div className="container-max">
        <div className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow-dark">
            {lang === "es" ? "Antes / Después" : "Before / After"}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-2 leading-tight">
            {lang === "es"
              ? "Calentador real — reparado de raíz, no parchado."
              : "Real water heater — rebuilt from the ground up, not patched."}
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl">
            {lang === "es"
              ? "Tubo de cobre corroído y tanque de expansión obstruido por sedimento. Repipeo completo, válvulas de cierre nuevas, manómetro nuevo, tanque nuevo. Mismo día, sin sorpresas."
              : "Corroded copper supply lines and a sediment-clogged expansion tank — fully repiped, new shut-offs, new gauge, new tank. Same day. No surprises."}
          </p>
        </div>

        <div className="mt-10 grid lg:grid-cols-[1.4fr_1fr] gap-6 items-stretch">
          <div
            ref={ref}
            data-testid="before-after-slider"
            className="relative w-full aspect-square sm:aspect-[4/3] rounded-3xl overflow-hidden border border-slate-200 bg-slate-100 shadow-lg select-none touch-none cursor-ew-resize"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            role="slider"
            aria-label="Before and after comparison"
            aria-valuenow={Math.round(pct)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            {/* AFTER fills the box */}
            <img
              src={afterImg}
              alt="Faster Plumbing — water heater after"
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            />
            {/* BEFORE clipped to the left of the divider */}
            <img
              src={beforeImg}
              alt="Faster Plumbing — water heater before"
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}
            />

            {/* Labels */}
            <div className="absolute top-4 left-4 bg-slate-900/85 backdrop-blur text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md">
              {lang === "es" ? "Antes" : "Before"}
            </div>
            <div className="absolute top-4 right-4 bg-brand-yellow text-slate-900 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md">
              {lang === "es" ? "Después" : "After"}
            </div>

            {/* Divider + handle */}
            <div
              className="absolute top-0 bottom-0 pointer-events-none"
              style={{ left: `${pct}%`, transform: "translateX(-50%)" }}
            >
              <div className="w-0.5 h-full bg-white shadow-[0_0_12px_rgba(0,0,0,0.45)]" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-xl ring-2 ring-brand-yellow flex items-center justify-center">
                <MoveHorizontal size={18} className="text-slate-900" />
              </div>
            </div>
          </div>

          {/* Job notes card */}
          <aside className="relative bg-white border border-slate-200 rounded-3xl p-7 sm:p-8 shadow-sm flex flex-col">
            <span className="absolute top-0 left-7 right-7 h-1 bg-brand-yellow rounded-b-md" />
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow-dark">
              {lang === "es" ? "Detalles del trabajo" : "Job notes"}
            </p>
            <h3 className="font-heading text-2xl font-bold text-slate-900 mt-2 leading-tight">
              {lang === "es"
                ? "Calentador A.O. Smith · repipe completo en cobre"
                : "A.O. Smith heater · full copper repipe"}
            </h3>
            <ul className="mt-4 space-y-3 text-slate-700 text-[15px] leading-relaxed">
              {(lang === "es"
                ? [
                    "Cambio de líneas corroídas por cobre tipo L nuevo",
                    "Tanque de expansión y manómetro de presión nuevos",
                    "Válvulas de bola de cuarto de vuelta en cada línea",
                    "Pruebas de presión y de fugas antes de cerrar",
                  ]
                : [
                    "Corroded lines swapped for new type-L copper",
                    "New expansion tank and pressure gauge installed",
                    "Quarter-turn ball valves on every supply line",
                    "Pressure-tested and leak-tested before sign-off",
                  ]).map((line) => (
                <li key={line} className="flex items-start gap-2.5">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-yellow shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <div className="mt-auto pt-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <span className="inline-flex items-center gap-1.5 bg-slate-100 px-2.5 py-1 rounded-md">
                {lang === "es" ? "Trabajo real" : "Real job"}
              </span>
              <span className="inline-flex items-center gap-1.5 bg-slate-100 px-2.5 py-1 rounded-md">
                {t.common.sameDay}
              </span>
              <span className="inline-flex items-center gap-1.5 bg-slate-100 px-2.5 py-1 rounded-md">
                {lang === "es" ? "Lake Worth Beach" : "Lake Worth Beach"}
              </span>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
